import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header, Skeleton, StatusMessage, Tab, TabNav } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { SearchX, TriangleAlert } from 'lucide-react';

import { DEFAULT_ROLE_TAB, ROLE_DETAIL_TABS, ROLE_TAB } from '../../constants/roles';
import { useRole } from '../../hooks/useRole';
import { useRoleMutations } from '../../hooks/useRoleMutations';
import { useStudiosCatalog } from '../../hooks/useStudiosCatalog';
import { getBackTo } from '../../utils/groupPaths';
import FullPageFrame from '../shared/FullPageFrame';
import DeleteRoleModal from './DeleteRoleModal';
import RoleDetailHeader from './RoleDetailHeader';
import RoleMembersTab from './RoleMembersTab';
import RolePermissionsTab from './RolePermissionsTab';

const ERROR_ICON = { notFound: SearchX, failed: TriangleAlert };

/**
 * Body of the role detail: page header, the role card (rename, re-describe, delete), the Members
 * and Permissions tabs and the delete dialog. Mount it with `key={roleId}`.
 *
 * @param {object} props
 * @param {string} props.roleId
 * @param {string} props.basePath Where the host mounted People & Access.
 */
function RoleDetailView({ roleId, basePath }) {
  const { t } = useTranslation('people-access');
  const navigate = useNavigate();
  // A member's Roles tab (Edit role) sets `state.backTo`; Back returns there instead of the roles list.
  const { state: linkState } = useLocation();
  const { role, isLoading, error, setRole, refetch } = useRole(roleId);
  const { studios, isLoading: studiosLoading, error: studiosError } = useStudiosCatalog();
  const { update, remove } = useRoleMutations();
  const [tab, setTab] = useState(DEFAULT_ROLE_TAB);
  const [dialog, setDialog] = useState(null);

  const listPath = `${basePath}/roles`;
  const closeDialog = useCallback(() => setDialog(null), []);
  const askDelete = useCallback(() => setDialog('delete'), []);
  const backTo = getBackTo(linkState);
  const goBack = useCallback(() => navigate(backTo ?? listPath), [navigate, backTo, listPath]);

  const handleSaveDetails = useCallback(
    async (patch) => {
      const result = await update(roleId, patch);
      if (result.ok) setRole(result.role);
      return result;
    },
    [update, roleId, setRole]
  );

  const handleSavePermissions = useCallback(
    async (patch) => {
      const result = await update(roleId, patch, {
        successKey: 'roles.detail.toast.permissionsSaved',
      });
      if (result.ok) setRole(result.role);
      return result.ok;
    },
    [update, roleId, setRole]
  );

  const handleDelete = useCallback(async () => {
    const ok = await remove(role);
    if (ok) navigate(listPath);
    return ok;
  }, [remove, role, navigate, listPath]);

  if (isLoading || (!error && studiosLoading)) {
    return (
      <FullPageFrame>
        <div className="flex flex-col gap-4" data-testid="role-detail-loading">
          <Skeleton width="220px" height="20px" />
          <Skeleton height="180px" borderRadius="12px" />
          <Skeleton height="240px" borderRadius="12px" />
        </div>
      </FullPageFrame>
    );
  }

  const failure = error ?? (studiosError ? 'failed' : null);
  if (failure) {
    return (
      <FullPageFrame>
        <StatusMessage
          icon={ERROR_ICON[failure]}
          title={t(`roles.detail.state.${failure}.title`)}
          subtitle={t(`roles.detail.state.${failure}.subtitle`)}
        />
      </FullPageFrame>
    );
  }

  return (
    <FullPageFrame>
      <Header
        title={t('roles.detail.pageTitle')}
        titleClassName="!text-lg !font-semibold"
        showBackButton
        onBack={goBack}
        backButtonAriaLabel={t('roles.detail.backAria')}
        showDescription={false}
        showTag={false}
        showPrimaryCta={false}
      />
      <RoleDetailHeader role={role} onSaveDetails={handleSaveDetails} onDelete={askDelete} />
      <TabNav ariaLabel={t('roles.detail.tabsAria')}>
        {ROLE_DETAIL_TABS.map(({ id, labelKey }) => (
          <Tab key={id} selected={tab === id} onClick={() => setTab(id)}>
            {t(labelKey)}
          </Tab>
        ))}
      </TabNav>
      {/* Both stay mounted so a permission edit in progress survives a visit to the other tab. */}
      <div hidden={tab !== ROLE_TAB.MEMBERS}>
        <RoleMembersTab role={role} onChanged={refetch} />
      </div>
      <div hidden={tab !== ROLE_TAB.PERMISSIONS}>
        <RolePermissionsTab role={role} studios={studios} onSave={handleSavePermissions} />
      </div>
      <DeleteRoleModal
        role={dialog === 'delete' ? role : null}
        onClose={closeDialog}
        onConfirm={handleDelete}
      />
    </FullPageFrame>
  );
}

export default RoleDetailView;
